import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Alert, Button, Snackbar } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { PAYMENT_METHODS } from "../../../constants/banks";

const EditExpense = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialData = location.state?.row || {};
  const [formData, setFormData] = useState(initialData);
  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [banks, setBanks] = useState([]);
  const { token } = useAuthStore();

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await fetch("/api/getBankDetails", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setBanks(data.bankDetails || []);
        }
      } catch (err) {
        console.error("Error fetching banks:", err);
      }
    };
    if (token) fetchBanks();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBankChange = (e) => {
    const selectedBank = banks.find(b => b._id === e.target.value);
    setFormData((prev) => ({
      ...prev,
      bankId: e.target.value,
      bankName: selectedBank ? selectedBank.accountname : "",
    }));
  };

  const handleSave = async () => {
    try {
      const response = await fetch(
        `/api/updateExpense/${formData.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update expense");
      }

      setSuccessMessage(true);
      setTimeout(() => navigate("/Expense"), 2000);
    } catch (error) {
      console.error("Error updating expense: ", error);
      setErrorMessage("Failed to update expense");
    }
  };

  const handleBack = () => navigate("/Expense");
  const handleCloseSnackbar = () => setSuccessMessage(false);

  return (
    <div className="p-8 bg-gray-100 rounded-lg shadow-lg max-w-7xl mx-auto">
      <button
        onClick={handleBack}
        className="flex items-center mb-4 text-blue-500 hover:underline"
      >
        <ArrowBackIcon className="mr-2" /> Back
      </button>

      <h2 className="text-3xl font-bold mb-6 justify-center">Edit Expense</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="col-span-2 grid grid-cols-2 gap-6">
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Expense Header
            </label>
            <input
              type="text"
              name="header"
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              value={formData.header || ""}
              disabled
            />
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Expense Subheader
            </label>
            <input
              type="text"
              name="subheader"
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              value={formData.taxRate ? `${formData.subheader} (${formData.taxRate}%)` : formData.subheader || ""}
              disabled
            />
          </div>
          <div className="col-span-2">
            <label className="block font-medium mb-2 text-blue-500">Date</label>
            <input
              type="date"
              name="date"
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.date?.split("T")[0] || ""}
              onChange={handleChange}
            />
          </div>

          <div className="col-span-2">
            <label className="block font-medium mb-2 text-blue-500">Amount</label>
            <input
              type="number"
              name="amount"
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.amount || ""}
              onChange={handleChange}
            />
          </div>

          <div className="col-span-2">
            <label className="block font-medium mb-2 text-blue-500">Bank Account</label>
            <select
              name="bankId"
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.bankId || ""}
              onChange={handleBankChange}
            >
              <option value="">Select bank account (optional)</option>
              {banks.map((bank) => (
                <option key={bank._id} value={bank._id}>
                  {bank.accountname} - {bank.accountnumber}
                </option>
              ))}
            </select>
          </div>

          <div className="col-span-2">
            <label className="block font-medium mb-2 text-blue-500">Payment Method</label>
            <select
              name="paymentMethod"
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.paymentMethod || ""}
              onChange={handleChange}
            >
              <option value="">Select payment method (optional)</option>
              {PAYMENT_METHODS.map((method) => (
                <option key={method.id} value={method.name}>
                  {method.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-span-2">
            <label className="block font-medium mb-2 text-blue-500">Description</label>
            <textarea
              name="description"
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.description || ""}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>

      {errorMessage && (
        <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-lg">
          {errorMessage}
        </div>
      )}

      <div className="mt-6">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          className="w-full md:w-auto px-6 py-2 align-center"
        >
          Save Changes
        </Button>
      </div>

      <Snackbar
        open={successMessage}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{
            width: "100%", color: "white", backgroundColor: "#22c55e", "& .MuiAlert-icon": {
              color: "white",
            },
          }}
        >
          Expense updated successfully!
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EditExpense;
